import {NumberField} from '@wandb/components';
import {Select} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useState} from 'react';
import {type SingleValue} from 'react-select';

import {useDebouncedValue} from '../../../../../util/useDebouncedValue';
import {
  useMediaMaxGalleryItemsDisplay,
  useShouldShowMediaMaxGalleryItemsResetAtLevel,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';
import {SettingsLevel} from '../types';

type MaxItemCountOption = {
  label: OptionLabel;
  value: number;
};

enum OptionLabel {
  SHOW_ALL = 'Show all',
  LIMIT_MEDIA = 'Limit media',
}

const MAX_ITEM_COUNT_OPTIONS: MaxItemCountOption[] = Object.values(
  OptionLabel
).map((label, index) => ({
  label,
  value: index,
}));

const DEFAULT_MAX_ITEM_COUNT = 25;

/** Level whose effective value this level falls back to when it has nothing set. */
const PARENT_SETTINGS_LEVEL: Record<SettingsLevel, SettingsLevel> = {
  [SettingsLevel.PANEL]: SettingsLevel.SECTION,
  [SettingsLevel.SECTION]: SettingsLevel.WORKSPACE,
  // The workspace has no parent; its display value is undefined whenever the
  // dropdown is on "Show all", so the lookup only ever yields the fallback.
  [SettingsLevel.WORKSPACE]: SettingsLevel.WORKSPACE,
};

export const MaxItemCount = () => {
  const {updateConfig, unsetConfigField, settingsLevel, panelRef} =
    useMediaSettingsContext();
  const maxGalleryItems = useMediaMaxGalleryItemsDisplay(
    settingsLevel,
    panelRef
  );
  // Seeds the number input when switching from "Show all" to "Limit media",
  // so the input starts from the limit the panel would otherwise inherit.
  const inheritedLimit = useMediaMaxGalleryItemsDisplay(
    PARENT_SETTINGS_LEVEL[settingsLevel],
    panelRef
  );
  const isSetAtLevel = useShouldShowMediaMaxGalleryItemsResetAtLevel(
    settingsLevel,
    panelRef
  );

  const [selectedOption, setSelectedOption] = useState(
    maxGalleryItems == null ? OptionLabel.SHOW_ALL : OptionLabel.LIMIT_MEDIA
  );

  // number = limit, null = "explicitly show all" (blocks an inherited limit),
  // undefined = unset at this level (fall back to the parent).
  const updateConfigCallback = useCallback(
    (value: number | null | undefined) => {
      if (value === undefined) {
        unsetConfigField('maxGalleryItems');
      } else {
        updateConfig({maxGalleryItems: value});
      }
    },
    [updateConfig, unsetConfigField]
  );

  const {
    value: debouncedMaxItemCount,
    debouncedSetValue: debouncedUpdateConfig,
    instantSetValue: instantUpdateConfig,
  } = useDebouncedValue<number | null | undefined>(
    maxGalleryItems,
    500,
    updateConfigCallback
  );

  const [maxItemCount, setMaxItemCount] = useState(debouncedMaxItemCount);

  const onChangeDropdown = (option: SingleValue<MaxItemCountOption>) => {
    if (option == null) {
      return;
    }
    setSelectedOption(option.label);
    if (option.label === OptionLabel.LIMIT_MEDIA) {
      if (maxGalleryItems == null) {
        const initialLimit = inheritedLimit ?? DEFAULT_MAX_ITEM_COUNT;
        setMaxItemCount(initialLimit);
        instantUpdateConfig(initialLimit);
      }
    } else {
      setMaxItemCount(undefined);
      instantUpdateConfig(null);
    }
  };

  const onChangeInput = (value?: number) => {
    if (value != null) {
      setMaxItemCount(value);
      debouncedUpdateConfig(value);
    }
  };
  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Media display limit"
        configField="maxGalleryItems"
        isDirty={isSetAtLevel}
        unsetConfigField={() => instantUpdateConfig(undefined)}
      />
      <div className="flex">
        <Select<MaxItemCountOption>
          className="w-full"
          aria-label="Media display limit"
          options={MAX_ITEM_COUNT_OPTIONS}
          value={MAX_ITEM_COUNT_OPTIONS.find(
            option => option.label === selectedOption
          )}
          onChange={onChangeDropdown}
        />
        {selectedOption === OptionLabel.LIMIT_MEDIA && (
          <div className="ml-8" data-test="max-item-count-input">
            <NumberField
              minValue={0}
              maxValue={108}
              step={1}
              value={maxItemCount ?? DEFAULT_MAX_ITEM_COUNT}
              onChange={onChangeInput}
            />
          </div>
        )}
      </div>
    </div>
  );
};
